import { useFormContext } from "react-hook-form";
import { SignupFormValues } from "@/shared/lib/validation/user-schemas";
import { Checkbox } from "@/shared/ui/checkbox";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/shared/ui/form";
import { Button } from "@/shared/ui/button";
import { Ico24ArrowRight } from "@/shared/assets/icons/ico_24_arrow_right";
import Link from "next/link";
import { useTranslations } from "next-intl";

const TERMS = [
  {
    id: "agreeTerms" as keyof SignupFormValues,
    label: "f_atc_cat_2",
    url: "/terms",
    required: true,
  },
  {
    id: "agreePrivacy" as keyof SignupFormValues,
    label: "f_atc_cat_3",
    url: "/privacy-policy",
    required: true,
  },
];

interface TermsStepProps {
  onClickTermsSubmit: () => void;
}

export function TermsStep({ onClickTermsSubmit }: TermsStepProps) {
  const t = useTranslations();
  const form = useFormContext<SignupFormValues>();
  // 전체 동의 상태 확인
  const allRequiredAgreed = TERMS.filter((t) => t.required).every((term) => form.watch(term.id) === true);
  const allTermsAgreed = TERMS.every((term) => form.watch(term.id) === true);
  // 전체 선택/해제
  const handleSelectAll = (checked: boolean) => {
    TERMS.forEach((term) => {
      form.setValue(term.id, checked);
    });
  };

  const handleSubmit = async () => {
    // 필수 약관 동의 체크
    const termsValid = await form.trigger(["agreeTerms", "agreePrivacy"]);

    if (termsValid && allRequiredAgreed) {
      onClickTermsSubmit();
    }
  };

  return (
    <div className="pc:max-w-[420px] w-full text-center">
      <h3 className="heading-3-bold tb:mb-3 mb-2 text-gray-900">{t("f_atc_title")}</h3>
      <div>
        <p className="body-md-regular text-gray-900">{t("f_atc_cont")}</p>
      </div>

      <div className="tb:mt-15 tb:space-y-4 mt-8 space-y-[10px]">
        {/* 전체 동의 체크박스 */}
        <div className="tb:pb-4 border-b border-gray-400 pb-[10px]">
          <FormItem className="tb:gap-2 flex items-center gap-[6px]">
            <Checkbox id="agreeAll" variant="public" checked={allTermsAgreed} onCheckedChange={handleSelectAll} />
            <FormLabel htmlFor="agreeAll" className="body-md-bold text-gray-900">
              {t("f_atc_cat_1")}
            </FormLabel>
          </FormItem>
        </div>

        {/* 개별 약관 */}
        <div className="tb:space-y-4 space-y-[10px]">
          {TERMS.map((term, index) => (
            <FormField
              key={term.id}
              control={form.control}
              name={term.id}
              render={({ field }) => (
                <FormItem className="tb:gap-2 flex flex-col items-start gap-[6px]">
                  <div className="flex items-center gap-[6px]">
                    <FormControl>
                      <Checkbox variant="public" checked={!!field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <FormLabel className="body-md-regular flex w-full items-center justify-between text-gray-900">
                      <span>{t(term.label)}</span>
                      {term.required && (
                        <Button variant="text_icon" size="sm" asChild>
                          <Link href={term.url ?? ""} target="_blank">
                            {t("f_atc_btn_cat_2")} <Ico24ArrowRight />
                          </Link>
                        </Button>
                      )}
                    </FormLabel>
                  </div>
                  {/* 두 번째 항목(agreePrivacy) 뒤에 에러 메시지 표시 */}
                  {index === 1 && (form.formState.errors.agreeTerms || form.formState.errors.agreePrivacy) && (
                    <div className="body-sm-regular ml-[30px] text-start text-red-500">
                      {form.formState.errors.agreeTerms?.message || form.formState.errors.agreePrivacy?.message}
                    </div>
                  )}
                </FormItem>
              )}
            />
          ))}
        </div>
      </div>

      <Button type="button" className="mt-10 w-full" variant="primary" size="lg" onClick={handleSubmit}>
        {t("f_atc_btn_nt")}
      </Button>
    </div>
  );
}
